import React, { useState, useMemo, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import RoleToggle from "../../components/RoleToggle";
import {
  IconPlus,
  IconSearch,
  IconX,
  IconUpload,
  IconDownload,
  IconPencil,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import useDebounce from "../../utilities/useDebounce";
import PremiumSearch from "../../components/common/PremiumSearch";
import {
  useFetchProductItemData,
  useItemProductCreateMutation,
  useItemsUpdateMutation,
  useProductCategoryAllListData,
  useUpdateProductItemStatus,
  useProductSubCategoriesListData,
} from "../../apis/productApis";
import {
  useFetchPromoItemData,
  useItemPromoCreateMutation,
  useUpdatePromoItemStatus,
  usePromoUpdateMutation,
} from "../../apis/promoApis";
import {
  useGiftCreateMutation,
  useFetchGiftData,
  useUpdateGiftStatusMutation,
  useDeleteGiftMutation,
  useUpdateGiftMutation,
} from "../../apis/giftApis";
import { useMerchantAllListData } from "../../apis/merchantApis";
import { utils, writeFile } from "xlsx";
import ItemBulkUploadModals from "../InventoryManagement/_components/ItemBulkUploadModals";
import ItemBulkResponseModals from "../InventoryManagement/_components/ItemBulkResponseModals";
import CreatePromoModal from "../InventoryManagement/_components/CreatePromoModal";
import CreateGiftForm from "../InventoryManagement/_components/CreateGiftForm";
import {
  Gift,
  Product,
  ProductFormState,
  Promo,
  VariantForm,
} from "../../types/types";
import { Catalog } from "./_components/Catalog";
import { Compiler } from "./_components/Compiler";
import { Gifts } from "./_components/Gifts";
import { Promos, PromoForm } from "./_components/Promos";
import { ProductDetailView } from "./_components/ProductDetailView";
import ProductFetch from "../InventoryManagement/_components/ProductFetch";
import { useLanguage } from "../../context/LanguageContext";
import DropdownFilter from "../../components/common/DropdownFilter";
import ButtonStyled from "../../components/common/ButtonStyled";

// Initial data arrays (strictly empty, all loaded dynamically from API)
const INITIAL_PRODUCTS: Product[] = [];
const INITIAL_PROMOS: Promo[] = [];
const INITIAL_GIFTS: Gift[] = [];
type ProductTab = "catalog" | "promos" | "gifts" | "productFetch";

const PRODUCT_TAB_LABELS: Record<ProductTab, string> = {
  catalog: "Product",
  promos: "Promos",
  gifts: "Gifts",
  productFetch: "Product Fetch",
};

const getInternalProductTab = (tab?: string): ProductTab | undefined => {
  if (!tab) return undefined;
  if (tab === "Product") return "catalog";
  if (tab === "Promos") return "promos";
  if (tab === "Gifts") return "gifts";
  if (tab === "Product Fetch") return "productFetch";
  if (["catalog", "promos", "gifts", "productFetch"].includes(tab)) {
    return tab as ProductTab;
  }
  return undefined;
};

const initialProductForm: ProductFormState = {
  sku: "",
  name: "",
  category: "",
  subcategory: "",
  price: "",
  discount: "",
  minOrder: "",
  shortDesc: "",
  longDesc: "",
  storageType: "",
  storageCapacity: "",
  storageTrigger: "",
  storageList: [{ type: "", capacity: "", minimumQuantity: "" }],
  complianceCommodity: "",
  complianceHsCode: "",
  complianceOrigin: "",
  complianceDestination: "",
  complianceTradeType: "",
  complianceDuty: "",
  complianceVat: "",
  complianceRestricted: "",
  complianceLicense: "",
  complianceDocuments: "",
  dimensionsLength: "",
  dimensionsWidth: "",
  dimensionsHeight: "",
  dimensionsWeight: "",
  terms: "",
  features: {
    variants: false,
    expiry: false,
    dangerGoods: false,
    terms: false,
    compliance: false,
  },
};

export default function ProductDemo({
  activeTabExternal,
  onTabChangeExternal,
  onFetchStatsChange,
}: {
  activeTabExternal?: string;
  onTabChangeExternal?: (tab: string) => void;
  onFetchStatsChange?: (
    stats: { total: number; active: number } | null,
  ) => void;
} = {}) {
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchFor, setSearchFor] = useState<string>("");
  const [promoCodeFilter, setPromoCodeFilter] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const debouncedSearchFor = useDebounce(searchFor, 550);
  const debouncedPromoCode = useDebounce(promoCodeFilter, 550);

  const [activeTabInternal, setActiveTabInternal] =
    useState<ProductTab>("catalog");

  const activeTab =
    getInternalProductTab(activeTabExternal) || activeTabInternal;

  const setActiveTab = (tab: ProductTab) => {
    setActiveTabInternal(tab);
    if (onTabChangeExternal) {
      onTabChangeExternal(PRODUCT_TAB_LABELS[tab]);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [value, setValue] = useState("All");

  const setDropdownValue = (data: string | undefined) => {
    setValue(data ?? "All");
  };

  const [editingProductId, setEditingProductId] = useState<string | undefined>(
    undefined,
  );
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState<boolean>(false);
  const [isBulkResponseOpen, setIsBulkResponseOpen] = useState<boolean>(false);
  const [itemBulkResponse, setItemBulkResponse] = useState<any>(null);
  const [formErrors, setFormErrors] = useState<
    Record<string, { message: string }>
  >({});
  const [isCompilerSidePanelOpen, setIsCompilerSidePanelOpen] =
    useState<boolean>(false);
  const [sidebarActiveTab, setSidebarActiveTab] = useState<string>("Product");
  const createGiftRef = useRef<{ submit: () => void }>(null);

  const handleViewProduct = (product: Product) => {
    setEditingProductId(undefined);
    setViewingProduct(product);
    setSidebarActiveTab("Product Details");
    setIsCompilerSidePanelOpen(true);
  };

  const {
    register: registerPromo,
    handleSubmit: handleSubmitPromo,
    formState: { errors: promoErrors },
    clearErrors: clearPromoErrors,
    reset: resetPromo,
    setValue: setPromoValue,
    watch: watchPromo,
  } = useForm({
    defaultValues: {
      toClient: "",
      promoCode: "",
      startEndDate: "",
      products: [{ productId: "", quantity: "" }],
    },
  });

  const onSubmitPromo = (values: any) => {
    const payload = {
      clientType: "LM",
      toClient: values.toClient,
      startEndDate: values.startEndDate,
      promoCode: values.promoCode,
      productAndQuantity: values.products.map((p: any) => ({
        productId: p.productId,
        quantity: parseInt(p.quantity),
      })),
    };
    if (editingPromoId) {
      updatePromo(
        { id: editingPromoId, ...payload },
        {
          onSuccess: () => {
            toast.success(t("Promo updated successfully"));
            resetPromo();
            setSidebarActiveTab("Product");
            setIsCompilerSidePanelOpen(false);
            setEditingPromoId(undefined);
            queryClient.invalidateQueries({ queryKey: ["itemPromoData"] });
          },
          onError: (err: any) => {
            toast.error(t(err?.message || "Failed to update promo"));
          },
        },
      );
    } else {
      createPromo(payload, {
        onSuccess: () => {
          toast.success(t("Promo created successfully"));
          resetPromo();
          setSidebarActiveTab("Product");
          setIsCompilerSidePanelOpen(false);
          queryClient.invalidateQueries({ queryKey: ["promoItemData"] });
        },
        onError: (err: any) => {
          toast.error(t(err?.message || "Failed to create promo"));
        },
      });
    }
  };

  // NEW: Close all panels when switching away from catalog
  useEffect(() => {
    if (activeTab !== "catalog") {
      setIsBulkUploadOpen(false);
      setIsBulkResponseOpen(false);
      setIsCompilerSidePanelOpen(false);
    }
  }, [activeTab]);

  // Promo State & Hooks
  const [promoPageNo] = useState<number>(1);
  const [promoSearchFor] = useState<string>("");
  const debouncedPromoSearch = useDebounce(promoSearchFor, 550);

  const { data: clientsResponse } = useMerchantAllListData();
  const clients = useMemo(() => {
    if (Array.isArray(clientsResponse)) return clientsResponse;
    if (Array.isArray(clientsResponse?.clients)) return clientsResponse.clients;
    if (Array.isArray(clientsResponse?.data?.clients))
      return clientsResponse.data.clients;
    if (Array.isArray(clientsResponse?.data)) return clientsResponse.data;
    return [];
  }, [clientsResponse]);

  const { data: promoItemsResponse, isLoading: isPromoLoading } =
    useFetchPromoItemData(promoPageNo, 50, debouncedPromoSearch);

  const { mutate: createPromo, isPending: isPromoCreating } =
    useItemPromoCreateMutation();

  const { mutate: updatePromoStatus } = useUpdatePromoItemStatus();
  const [editingPromoId, setEditingPromoId] = useState<string | undefined>(
    undefined,
  );
  const { mutate: updatePromo } = usePromoUpdateMutation();
  const { mutate: updateGiftStatus } = useUpdateGiftStatusMutation();

  const handleTogglePromoStatus = (id: string, currentStatus: string) => {
    const nextStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    updatePromoStatus(
      { id, status: nextStatus },
      {
        onSuccess: () => {
          toast.success(t("Updated successfully"));
          queryClient.invalidateQueries({ queryKey: ["itemPromoData"] });
          queryClient.invalidateQueries({ queryKey: ["promoItemData"] });
        },
        onError: (err: any) => {
          toast.error(err?.message || t("Failed to update promo status"));
        },
      },
    );
  };

  const handleToggleGiftStatus = (id: string, currentStatus: string) => {
    const nextStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    updateGiftStatus(
      { id, status: nextStatus },
      {
        onSuccess: () => {
          toast.success(t("Updated successfully"));
          queryClient.invalidateQueries({ queryKey: ["giftRuleData"] });
        },
        onError: (err: any) => {
          toast.error(err?.message || t("Failed to update gift status"));
        },
      },
    );
  };

  const handleEditPromo = (promo: Promo) => {
    setViewingProduct(null);
    setEditingPromoId(promo.id);
    setSidebarActiveTab("Promos");
    setIsCompilerSidePanelOpen(true);
    resetPromo({
      toClient: promo.toClient,
      promoCode: promo.sku,
      startEndDate:
        promo.startEndDate ||
        (promo.startDate && promo.endDate
          ? `${promo.startDate} to ${promo.endDate}`
          : ""),
      products: promo.products.map((p: any) => ({
        productId: p.productId || p.productSku,
        quantity: String(p.qty),
      })),
    });
  };

  const [selectedGiftForEdit, setSelectedGiftForEdit] = useState<
    Gift | undefined
  >(undefined);

  const handleEditGift = (gift: Gift) => {
    setViewingProduct(null);
    setEditingGiftId(gift.id);
    setSelectedGiftForEdit(gift);
    setGiftForm({
      merchant: gift.merchant || "",
      merchantId: gift.clientId || gift.merchantId || gift.toClient || "",
      giftSku: gift.giftSku || "",
      mainProductSku: gift.mainProductSku || "",
      mainQty: gift.mainQty || 1,
      giftProductSku: gift.giftProductSku || "",
      giftQty: gift.giftQty || 1,
      startDate: gift.startDate || "",
      endDate: gift.endDate || "",
      giftItems:
        gift.giftItems && gift.giftItems.length > 0
          ? gift.giftItems.map((g: any) => ({
              sku: g.sku || g.giftProductSku || "",
              qty: Number(g.qty || g.giftQty) || 1,
            }))
          : [
              {
                sku: gift.giftProductSku || "",
                qty: Number(gift.giftQty) || 1,
              },
            ],
    });
    setSidebarActiveTab("Gifts");
    setIsCompilerSidePanelOpen(true);
  };

  const handleSaveGiftFromForm = async (values: any) => {
    const startDate = values.startDate || "";
    const endDate = values.endDate || "";
    const merchantName =
      values.merchant ||
      clients.find((c: any) => c.id === (values.toClient || values.merchantId))
        ?.name ||
      selectedGiftForEdit?.merchant ||
      "";
    const giftSkuToUse = (
      values.giftSku ||
      selectedGiftForEdit?.giftSku ||
      giftForm.giftSku ||
      ""
    ).trim();
    const clientIdToUse =
      values.toClient ||
      values.merchantId ||
      selectedGiftForEdit?.clientId ||
      selectedGiftForEdit?.merchantId ||
      "";

    const itemsToSave =
      values.giftItems && values.giftItems.length > 0
        ? values.giftItems.filter((g: any) => (g.sku || "").trim() !== "")
        : [{ sku: values.giftProductSku, qty: values.giftQty }];

    if (itemsToSave.length === 0) return;

    try {
      if (editingGiftId) {
        // Get the current grouped campaign (all sibling IDs from selectedGiftForEdit)
        const siblingItems: Array<{ id: string; sku: string; qty: number }> =
          selectedGiftForEdit?.giftItems || [
            {
              id: editingGiftId,
              sku: selectedGiftForEdit?.giftProductSku || "",
              qty: selectedGiftForEdit?.giftQty || 1,
            },
          ];

        const commonPayload = {
          clientId: clientIdToUse,
          merchant: merchantName,
          giftSku: giftSkuToUse,
          mainProductSku: (values.mainProductSku || "").trim(),
          mainQty: Number(values.mainQty) || 1,
          startDate,
          endDate,
        };

        const promises: Promise<any>[] = [];

        // Update existing sibling records or create new ones
        itemsToSave.forEach((item: any, idx: number) => {
          const existingSibling = siblingItems[idx];
          if (existingSibling?.id) {
            promises.push(
              updateGiftMutationAsync({
                id: existingSibling.id,
                payload: {
                  ...commonPayload,
                  giftProductSku: item.sku.trim(),
                  giftQty: Number(item.qty) || 1,
                },
              }),
            );
          } else {
            promises.push(
              createGiftAsync({
                ...commonPayload,
                giftProductSku: item.sku.trim(),
                giftQty: Number(item.qty) || 1,
              }),
            );
          }
        });

        // Delete sibling records that were removed from the UI form
        siblingItems.slice(itemsToSave.length).forEach((sibling: any) => {
          if (sibling?.id) {
            promises.push(deleteGiftMutationAsync(sibling.id));
          }
        });

        await Promise.all(promises);
        toast.success(t("Gift rule updated successfully"));
      } else {
        const promises = itemsToSave.map((item: any) => {
          const payload = {
            clientId: clientIdToUse,
            merchant: merchantName,
            giftSku: giftSkuToUse,
            mainProductSku: (values.mainProductSku || "").trim(),
            mainQty: Number(values.mainQty) || 1,
            giftProductSku: item.sku ? item.sku.trim() : "",
            giftQty: item.qty ? Number(item.qty) || 1 : 0,
            startDate,
            endDate,
          };
          return createGiftAsync(payload);
        });

        await Promise.all(promises);
        toast.success(t("Gift rule(s) created successfully"));
      }

      queryClient.invalidateQueries({ queryKey: ["giftRuleData"] });
      setSidebarActiveTab("Product");
      setIsCompilerSidePanelOpen(false);
      setEditingGiftId(undefined);
      setSelectedGiftForEdit(undefined);
      resetGiftForm();
    } catch (err: any) {
      toast.error(err?.message || t("Failed to save gift rule"));
    }
  };

  // Gift Hooks
  const [editingGiftId, setEditingGiftId] = useState<string | undefined>(
    undefined,
  );
  const { data: giftRulesResponse } = useFetchGiftData(1, 50, "");
  const { mutateAsync: createGiftAsync } = useGiftCreateMutation();
  const { mutateAsync: updateGiftMutationAsync } = useUpdateGiftMutation();
  const { mutateAsync: deleteGiftMutationAsync } = useDeleteGiftMutation();

  // API Hooks — when promoCode filter is active, clear searchFor so it doesn't double-filter
  const { data: apiProductResponse, isLoading: isProductsLoading } =
    useFetchProductItemData(
      pageNo,
      50,
      debouncedPromoCode ? "" : debouncedSearchFor,
      debouncedPromoCode || undefined,
    );

  const totalItems = apiProductResponse?.total || 0;
  const totalPages = apiProductResponse?.pages || 1;

  const { data: categoriesData } = useProductCategoryAllListData();

  const { mutate: createProductItem, isPending: isCreating } =
    useItemProductCreateMutation();

  const { mutate: updateProductItem, isPending: isUpdating } =
    useItemsUpdateMutation(editingProductId || "");

  const { mutate: updateProductStatus } = useUpdateProductItemStatus();

  // Map API product items to UI Product model
  const products: Product[] = useMemo(() => {
    const rawItems =
      apiProductResponse?.productItems ||
      apiProductResponse?.data ||
      (Array.isArray(apiProductResponse) ? apiProductResponse : []);
    if (!Array.isArray(rawItems)) return [];

    return rawItems.map((item: any) => {
      let parsedStorage: any = { type: "BINS", capacity: 100, trigger: 10 };
      if (item.storage) {
        try {
          const s =
            typeof item.storage === "string"
              ? JSON.parse(item.storage)
              : item.storage;
          if (Array.isArray(s)) {
            parsedStorage = s.map((entry: any) => ({
              type: entry.type || entry.storageType || "BINS",
              capacity: Number(entry.capacity || 0),
              trigger: Number(entry.minimumQuantity || entry.trigger || 0),
            }));
          } else {
            parsedStorage = {
              type: s.type || s.storageType || "BINS",
              capacity: Number(s.capacity || 100),
              trigger: Number(s.minimumQuantity || s.trigger || 10),
            };
          }
        } catch {
          // fallback
        }
      }

      const allMediaUrls =
        Array.isArray(item.images) && item.images.length > 0
          ? item.images
          : item.image
            ? [item.image]
            : [];

      const rawImg =
        allMediaUrls.find((u: any) => {
          const str = typeof u === "string" ? u : u?.preview || u?.url || "";
          return (
            str &&
            !str.endsWith(".mp4") &&
            !str.endsWith(".webm") &&
            !str.endsWith(".mov")
          );
        }) ||
        (typeof allMediaUrls[0] === "string"
          ? allMediaUrls[0]
          : allMediaUrls[0]?.preview ||
            allMediaUrls[0]?.url ||
            item.image ||
            "");

      const mappedMediaList = allMediaUrls.map((u: any) => {
        const urlStr = typeof u === "string" ? u : u?.preview || u?.url || "";
        const isVid =
          urlStr.endsWith(".mp4") ||
          urlStr.endsWith(".webm") ||
          urlStr.endsWith(".mov");
        return {
          file: urlStr,
          preview: urlStr,
          type: isVid ? "video" : "image",
          isExisting: true,
        };
      });

      return {
        id: item.id || `prod-${Math.random()}`,
        name: item.itemName || item.name || "Untitled Product",
        sku:
          item.sku && item.sku !== "N/A"
            ? item.sku
            : item.productSku ||
              item.code ||
              item.itemName ||
              item.name ||
              item.id ||
              "SKU",
        categoryId: item.categoryId || item.category_id || "",
        category: item.categoryName || item.category || "General",
        subCategory:
          item.subCategoryId || item.subCategory || item.sub_category || "",
        subcategory:
          item.subCategoryId ||
          item.subCategory ||
          item.sub_category ||
          item.subCategoryName ||
          "General",
        status: item.status || "ACTIVE",
        price: Number(
          item.price1 !== undefined && item.price1 !== null
            ? item.price1
            : item.price !== undefined && item.price !== null
              ? item.price
              : item.price2 || 0,
        ),
        discount: Number(item.discount || 0),
        minOrder: Number(
          item.minimumOrder !== undefined && item.minimumOrder !== null
            ? item.minimumOrder
            : item.minOrder || 1,
        ),
        expiryDate: item.expiryDate || item.expiry_date || "",
        image:
          typeof rawImg === "string"
            ? rawImg
            : rawImg?.preview || rawImg?.url || "",
        mediaList: mappedMediaList,
        features: {
          hasVariants: Boolean(
            (item.variants && item.variants.length > 0) || item.hasVariants,
          ),
          hasExpiry: Boolean(item.hasExpiry || item.expiryBlock),
          isDangerousGoods: Boolean(
            item.dangerousGoods === "true" ||
            item.dangerousGoods === true ||
            item.isDangerousGoods,
          ),
          hasTerms: Boolean(item.termsAndConditions || item.hasTerms),
          hasCompliance: Boolean(
            item.hasCompliance ||
            item.compliance ||
            (item.hsCode && item.hsCode !== "—") ||
            (item.commodity && item.commodity !== "—") ||
            (item.importCommodity && item.importCommodity !== "—") ||
            item.origin ||
            item.destination ||
            item.tradeType ||
            item.dutyPercent !== undefined ||
            item.vatPercent !== undefined ||
            item.restricted ||
            item.license ||
            (item.documents && item.documents !== "—"),
          ),
        },
        storage: parsedStorage,
        compliance:
          item.hasCompliance ||
          item.compliance ||
          (item.hsCode && item.hsCode !== "—") ||
          (item.commodity && item.commodity !== "—") ||
          (item.importCommodity && item.importCommodity !== "—") ||
          item.origin ||
          item.destination ||
          item.tradeType ||
          (item.dutyPercent !== undefined &&
            item.dutyPercent !== null &&
            Number(item.dutyPercent) > 0) ||
          (item.vatPercent !== undefined &&
            item.vatPercent !== null &&
            Number(item.vatPercent) > 0) ||
          (item.license &&
            item.license !== "Not Required" &&
            item.license !== "—") ||
          (item.documents && item.documents !== "—")
            ? {
                commodity:
                  item.commodity && item.commodity !== "—"
                    ? item.commodity
                    : "",
                hsCode: item.hsCode && item.hsCode !== "—" ? item.hsCode : "",
                origin: item.origin && item.origin !== "—" ? item.origin : "",
                destination:
                  item.destination && item.destination !== "—"
                    ? item.destination
                    : "",
                tradeType:
                  item.tradeType && item.tradeType !== "—"
                    ? item.tradeType
                    : "",
                duty:
                  item.dutyPercent !== undefined &&
                  item.dutyPercent !== null &&
                  Number(item.dutyPercent) > 0
                    ? item.dutyPercent
                    : item.duty && item.duty !== "—" && Number(item.duty) > 0
                      ? item.duty
                      : "",
                vat:
                  item.vatPercent !== undefined &&
                  item.vatPercent !== null &&
                  Number(item.vatPercent) > 0
                    ? item.vatPercent
                    : item.vat && item.vat !== "—" && Number(item.vat) > 0
                      ? item.vat
                      : "",
                restricted: item.restricted ? "Yes" : "",
                license:
                  item.license &&
                  item.license !== "Not Required" &&
                  item.license !== "—"
                    ? item.license
                    : "",
                documents:
                  item.documents && item.documents !== "—"
                    ? item.documents
                    : "",
              }
            : null,
        dimensions: {
          length: String(item.length ?? "—"),
          width: String(item.width ?? "—"),
          height: String(item.height ?? "—"),
          weight: String(item.weight ?? "—"),
        },
        variants: Array.isArray(item.variants)
          ? item.variants.map((v: any) => ({
              sku: v.sku || "",
              name: v.name || v.optionName || "Variant",
              value: v.optionValue || v.value || "",
              price: Number(v.price || 0),
              image: v.image || v.variantImage || v.optionImage || undefined,
            }))
          : [],
        shortDesc:
          item.shortDescription || item.shortDesc || "No summary description.",
        longDesc: item.longDescription || item.longDesc || "",
        terms: item.termsAndConditions || "",
      };
    });
  }, [apiProductResponse]);

  // Map API promo items to UI Promo model
  const promos: Promo[] = useMemo(() => {
    const rawPromos =
      promoItemsResponse?.productItems ||
      promoItemsResponse?.promos ||
      promoItemsResponse?.data?.productItems ||
      promoItemsResponse?.data?.promos ||
      promoItemsResponse?.data ||
      (Array.isArray(promoItemsResponse) ? promoItemsResponse : []);
    if (!Array.isArray(rawPromos)) return [];

    return rawPromos.map((p: any) => {
      let startDate = "";
      let endDate = "";
      if (typeof p.startEndDate === "string") {
        const parts = p.startEndDate.split(/\s+to\s+|\s+-\s+|\s+,\s+/);
        startDate = parts[0]?.trim() || "";
        endDate = parts[1]?.trim() || "";
      } else if (Array.isArray(p.startEndDate) && p.startEndDate.length >= 2) {
        startDate = p.startEndDate[0]
          ? String(p.startEndDate[0]).split("T")[0]
          : "";
        endDate = p.startEndDate[1]
          ? String(p.startEndDate[1]).split("T")[0]
          : "";
      }

      const productsList = Array.isArray(p.products)
        ? p.products.map((item: any) => {
            const pid = item.productId || item.id || item._id || "";
            const rawSku =
              item.productSku || item.sku || item.productName || "";
            const foundProd = products.find(
              (prod) => prod.id === pid || (rawSku && prod.sku === rawSku),
            );
            return {
              productId: pid,
              productSku: foundProd?.sku || rawSku || pid || "—",
              qty: Number(item.quantity || item.qty || 1),
            };
          })
        : Array.isArray(p.productAndQuantity)
          ? p.productAndQuantity.map((pq: any) => {
              const pid = pq.productItem?.id || pq.productId || pq.id || "";
              const rawSku =
                pq.productItem?.sku ||
                pq.productSku ||
                pq.sku ||
                pq.productName ||
                "";
              const foundProd = products.find(
                (prod) => prod.id === pid || (rawSku && prod.sku === rawSku),
              );
              return {
                productId: pid,
                productSku: foundProd?.sku || rawSku || pid || "—",
                qty: Number(pq.quantity || pq.qty || 1),
              };
            })
          : [];

      return {
        id: p.id || `promo-${Math.random()}`,
        sku: p.promoCode || p.sku || "PROMO-001",
        promoCode: p.promoCode || p.sku || "PROMO-001",
        toClient: p.client?.id || p.clientId || p.toClient || "",
        merchant:
          p.client?.companyName || p.clientName || p.toClient || "Merchant",
        products: productsList,
        startDate: startDate,
        endDate: endDate,
        status: p.status || "ACTIVE",
        startEndDate: p.startEndDate || "",
      };
    });
  }, [promoItemsResponse, products]);

  // Map API gift rules to UI Gift model — group by campaign (giftSku + mainProductSku + clientId)
  const gifts: Gift[] = useMemo(() => {
    const rawGifts =
      giftRulesResponse?.gifts ||
      giftRulesResponse?.data?.gifts ||
      giftRulesResponse?.data ||
      (Array.isArray(giftRulesResponse) ? giftRulesResponse : []);
    if (!Array.isArray(rawGifts)) return [];

    // Group by campaign key: giftSku + mainProductSku + clientId
    const campaignMap = new Map<string, Gift>();

    rawGifts.forEach((g: any) => {
      const campaignKey = `${g.giftSku || ""}__${g.mainProductSku || ""}__${g.clientId || ""}`;

      if (campaignMap.has(campaignKey)) {
        // Append to existing campaign's giftItems
        const existing = campaignMap.get(campaignKey)!;
        existing.giftItems = existing.giftItems || [
          {
            id: existing.id,
            sku: existing.giftProductSku,
            qty: existing.giftQty,
          },
        ];
        existing.giftItems.push({
          id: g.id || "",
          sku: g.giftProductSku || "",
          qty: Number(g.giftQty || 1),
        });
      } else {
        campaignMap.set(campaignKey, {
          id: g.id || `gift-${Math.random()}`,
          merchant: g.merchant || "Merchant",
          merchantId: g.clientId || g.toClient || g.merchantId || "",
          giftSku: g.giftSku || "GIFT-001",
          mainProductSku: g.mainProductSku || "Main Product",
          mainQty: Number(g.mainQty || 1),
          giftProductSku: g.giftProductSku || "",
          giftQty: Number(g.giftQty || 1),
          startDate: g.startDate || "",
          endDate: g.endDate || "",
          status: g.status || "ACTIVE",
          giftItems: [
            {
              id: g.id || "",
              sku: g.giftProductSku || "",
              qty: Number(g.giftQty || 1),
            },
          ],
        });
      }
    });

    return Array.from(campaignMap.values());
  }, [giftRulesResponse]);

  const filterHeadings = useMemo(() => {
    const headings = [{ id: "", type: "All", value: "All" }];
    if (Array.isArray(categoriesData)) {
      categoriesData.forEach((c: any) => {
        headings.push({
          id: c.id || c.category,
          type: c.category,
          value: c.category,
        });
      });
    }
    return headings;
  }, [categoriesData]);

  // Form states
  const [productForm, setProductForm] =
    useState<ProductFormState>(initialProductForm);
  const [formVariants, setFormVariants] = useState<VariantForm[]>([]);
  const [imagePreview, setImagePreview] = useState("");

  const selectedCategoryObj = React.useMemo(() => {
    if (!Array.isArray(categoriesData) || !productForm.category) return null;
    return categoriesData.find(
      (c: any) =>
        c.id === productForm.category ||
        c.category === productForm.category ||
        c.categoryName === productForm.category,
    );
  }, [categoriesData, productForm.category]);

  const catParamById = selectedCategoryObj?.id || productForm.category || "";
  const catParamByName =
    selectedCategoryObj?.category ||
    selectedCategoryObj?.categoryName ||
    productForm.category ||
    "";

  const { data: subCategoriesById } =
    useProductSubCategoriesListData(catParamById);
  const { data: subCategoriesByName } =
    useProductSubCategoriesListData(catParamByName);

  const subCategoriesData = React.useMemo(() => {
    if (Array.isArray(subCategoriesById) && subCategoriesById.length > 0) {
      return subCategoriesById;
    }
    if (Array.isArray(subCategoriesByName) && subCategoriesByName.length > 0) {
      return subCategoriesByName;
    }
    return [];
  }, [subCategoriesById, subCategoriesByName]);

  const [promoForm, setPromoForm] = useState<PromoForm>({
    merchant: "",
    merchantId: "",
    sku: "",
    selectedProducts: [{ productSku: "", qty: 1 }],
    startDate: "",
    endDate: "",
  });

  const [giftForm, setGiftForm] = useState({
    merchant: "",
    merchantId: "",
    giftSku: "",
    mainProductSku: "",
    mainQty: 1,
    giftProductSku: "",
    giftQty: 1,
    startDate: "",
    endDate: "",
    giftItems: [{ sku: "", qty: 1 }],
  });

  // Toast helper
  const addToast = (
    msg: string,
    type: "success" | "error" | "info" = "success",
  ) => {
    if (type === "success") toast.success(msg);
    else if (type === "error") toast.error(msg);
    else toast(msg);
  };

  // Financial metrics
  const financialMetrics = useMemo(() => {
    const price = Number(productForm.price) || 0;
    const discount = Number(productForm.discount) || 0;
    const dutyPercent = productForm.features.compliance
      ? Number(productForm.complianceDuty) || 0
      : 0;
    const vatPercent = productForm.features.compliance
      ? Number(productForm.complianceVat) || 0
      : 0;
    const finalPrice = Math.max(0, price - discount);
    const estimatedDuty = finalPrice * (dutyPercent / 100);
    const estimatedVat = finalPrice * (vatPercent / 100);
    const landedCost = finalPrice + estimatedDuty;
    const grossTax = estimatedDuty + estimatedVat;
    return {
      finalPrice,
      estimatedDuty: estimatedDuty.toFixed(2),
      estimatedVat: estimatedVat.toFixed(2),
      landedCost: landedCost.toFixed(2),
      grossTax: grossTax.toFixed(2),
    };
  }, [
    productForm.price,
    productForm.discount,
    productForm.features.compliance,
    productForm.complianceDuty,
    productForm.complianceVat,
  ]);

  // Reset form
  const resetProductForm = () => {
    setEditingProductId(undefined);
    setViewingProduct(null);
    setProductForm(initialProductForm);
    setFormVariants([]);
    setImagePreview("");
    setFormErrors({});
    setSidebarActiveTab("Product");
  };

  // Edit Product Handler – now switches to catalog and opens compiler panel
  const handleEditProduct = (p: Product) => {
    setActiveTab("catalog"); // switch to catalog first
    setViewingProduct(null);
    setEditingProductId(p.id);
    setSidebarActiveTab("Product");
    setIsCompilerSidePanelOpen(true);
    const parsedStorageList = Array.isArray(p.storage)
      ? p.storage.map((s: any) => ({
          type: (s.type || s.storageType || "PALLETS").toString().toUpperCase(),
          capacity: String(s.capacity || ""),
          minimumQuantity: String(s.minimumQuantity || s.trigger || ""),
        }))
      : [
          {
            type: (p.storage?.type || "PALLETS").toString().toUpperCase(),
            capacity: String(p.storage?.capacity || ""),
            minimumQuantity: String(p.storage?.trigger || ""),
          },
        ];

    const initialMedia =
      p.mediaList && p.mediaList.length > 0
        ? p.mediaList
        : p.image
          ? [
              {
                file: p.image,
                preview: p.image,
                type: "image",
                isExisting: true,
              },
            ]
          : [];

    setProductForm({
      sku: p.sku !== "N/A" ? p.sku : "",
      name: p.name,
      category: p.category || p.categoryId || "",
      subcategory: p.subCategory || p.subCategoryId || "",
      price: p.price !== undefined && p.price !== null ? String(p.price) : "",
      discount:
        p.discount !== undefined && p.discount !== null
          ? String(p.discount)
          : "0",
      minOrder:
        p.minOrder !== undefined && p.minOrder !== null
          ? String(p.minOrder)
          : "1",
      expiryDate: p.expiryDate || "",
      shortDesc: p.shortDesc || "",
      longDesc: p.longDesc || "",
      mediaList: initialMedia,
      storageType: parsedStorageList[0]?.type || "PALLETS",
      storageCapacity: parsedStorageList[0]?.capacity || "100",
      storageTrigger: parsedStorageList[0]?.minimumQuantity || "10",
      storageList: parsedStorageList,
      complianceCommodity:
        p.compliance?.commodity && p.compliance.commodity !== "—"
          ? p.compliance.commodity
          : "",
      complianceHsCode:
        p.compliance?.hsCode && p.compliance.hsCode !== "—"
          ? p.compliance.hsCode
          : "",
      complianceOrigin:
        p.compliance?.origin && p.compliance.origin !== "—"
          ? p.compliance.origin
          : "",
      complianceDestination:
        p.compliance?.destination && p.compliance.destination !== "—"
          ? p.compliance.destination
          : "",
      complianceTradeType:
        p.compliance?.tradeType && p.compliance.tradeType !== "—"
          ? p.compliance.tradeType
          : "",
      complianceDuty:
        p.compliance?.duty && p.compliance.duty !== "—"
          ? String(p.compliance.duty)
          : "0",
      complianceVat:
        p.compliance?.vat && p.compliance.vat !== "—"
          ? String(p.compliance.vat)
          : "15",
      complianceRestricted: p.compliance?.restricted || "No",
      complianceLicense: p.compliance?.license || "Not Required",
      complianceDocuments:
        p.compliance?.documents && p.compliance.documents !== "—"
          ? p.compliance.documents
          : "",
      dimensionsLength:
        p.dimensions?.length !== "—" ? p.dimensions?.length || "" : "",
      dimensionsWidth:
        p.dimensions?.width !== "—" ? p.dimensions?.width || "" : "",
      dimensionsHeight:
        p.dimensions?.height !== "—" ? p.dimensions?.height || "" : "",
      dimensionsWeight:
        p.dimensions?.weight !== "—" ? p.dimensions?.weight || "" : "",
      terms: p.terms || "",
      features: {
        variants: Boolean(
          p.features?.hasVariants || (p.variants && p.variants.length > 0),
        ),
        expiry: Boolean(p.features?.hasExpiry || p.expiryDate),
        dangerGoods: Boolean(
          p.features?.isDangerousGoods || (p as any).dangerousGoods,
        ),
        terms: Boolean(p.features?.hasTerms || (p as any).termsAndConditions),
        compliance: Boolean(
          p.features?.hasCompliance ||
          p.compliance ||
          ((p.compliance as any)?.commodity &&
            (p.compliance as any).commodity !== "—") ||
          ((p.compliance as any)?.hsCode &&
            (p.compliance as any).hsCode !== "—") ||
          ((p.compliance as any)?.origin &&
            (p.compliance as any).origin !== "—") ||
          ((p.compliance as any)?.destination &&
            (p.compliance as any).destination !== "—") ||
          ((p.compliance as any)?.documents &&
            (p.compliance as any).documents !== "—") ||
          (p as any).origin ||
          (p as any).destination ||
          (p as any).hsCode,
        ),
      },
      ...(p.itemInfo || {}),
    });
    if (p.variants && p.variants.length > 0) {
      setFormVariants(
        p.variants.map((v: any) => ({
          sku: v.sku || "",
          name: v.name || "Variant",
          value: v.value || "",
          price: String(
            v.price !== undefined && v.price !== null ? v.price : "",
          ),
          image: v.image || "",
        })),
      );
    } else {
      setFormVariants([]);
    }
    setImagePreview(initialMedia[0]?.preview || p.image || "");
    // Open compiler side panel and close any bulk panels
    setIsBulkUploadOpen(false);
    setIsBulkResponseOpen(false);
    setIsCompilerSidePanelOpen(true);
  };

  // Create / Update Product Handler (unchanged)
  const handleAddProduct = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, { message: string }> = {};

    if (!productForm.name) {
      newErrors.name = { message: "This field is required" };
    }
    if (!productForm.sku) {
      newErrors.sku = { message: "This field is required" };
    }
    if (!productForm.price) {
      newErrors.price = { message: "This field is required" };
    }
    if (!productForm.dimensionsWeight) {
      newErrors.dimensionsWeight = { message: "This field is required" };
    }

    let categoryId = "";
    if (productForm.category) {
      if (Array.isArray(categoriesData) && categoriesData.length > 0) {
        const found = categoriesData.find(
          (c: any) =>
            c.id === productForm.category ||
            c.category === productForm.category ||
            c.categoryName === productForm.category,
        );
        categoryId = found?.id || productForm.category;
      } else {
        categoryId = productForm.category;
      }
    }

    if (!categoryId) {
      newErrors.category = { message: "This field is required" };
    }

    productForm.storageList?.forEach((st, idx) => {
      if (!st.type) newErrors[`storage_${idx}_type`] = { message: "Required" };
      if (!st.capacity)
        newErrors[`storage_${idx}_capacity`] = { message: "Required" };
      if (!st.minimumQuantity)
        newErrors[`storage_${idx}_minimumQuantity`] = { message: "Required" };
    });

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      addToast("Please fill in all required fields.", "error");
      return;
    }

    setFormErrors({});

    // Validate duplicate variant option values
    if (productForm.features.variants && formVariants.length > 0) {
      const optionValues = formVariants.map((v) =>
        (v.value || "").trim().toLowerCase(),
      );
      const duplicates = optionValues.filter(
        (val, idx) => optionValues.indexOf(val) !== idx,
      );
      if (duplicates.length > 0) {
        addToast(
          `Duplicate variant value "${duplicates[0]}" found. Each variant must have a unique option value.`,
          "error",
        );
        return;
      }
    }

    // ─── SKU Uniqueness Validation ─────────────────────────────
    const formSkus: { sku: string; label: string }[] = [];
    const mainTrimmedSku = (productForm.sku || "").trim();
    if (mainTrimmedSku) {
      formSkus.push({ sku: mainTrimmedSku, label: "Product SKU" });
    }

    if (productForm.features.variants && formVariants.length > 0) {
      formVariants.forEach((v, idx) => {
        const vSku = (v.sku || "").trim();
        if (vSku) {
          formSkus.push({
            sku: vSku,
            label: `Variant ${idx + 1} SKU (${v.value || v.name || "Variant"})`,
          });
        }
      });
    }

    const seenLowerSkus = new Map<string, string>();
    for (const item of formSkus) {
      const lower = item.sku.toLowerCase();
      if (seenLowerSkus.has(lower)) {
        addToast(
          `Duplicate SKU "${item.sku}" detected (${seenLowerSkus.get(lower)} & ${item.label}). All SKUs must be unique.`,
          "error",
        );
        return;
      }
      seenLowerSkus.set(lower, item.label);
    }

    const storagePayload =
      productForm.storageList && productForm.storageList.length > 0
        ? productForm.storageList.map((s) => ({
            type: s.type || "PALLETS",
            capacity: s.capacity || "0",
            minimumQuantity: Number(s.minimumQuantity) || 0,
          }))
        : [
            {
              type: productForm.storageType || "PALLETS",
              capacity: productForm.storageCapacity || "0",
              minimumQuantity: Number(productForm.storageTrigger) || 0,
            },
          ];

    const newImages: File[] = [];
    const remainingImages: string[] = [];
    let newVideo: File | undefined = undefined;
    let remainingVideo: string | undefined = undefined;
    let primaryImage: File | string | undefined = undefined;

    if (productForm.mediaList && productForm.mediaList.length > 0) {
      productForm.mediaList.forEach((m: any) => {
        if (m.type === "image") {
          if (m.file instanceof File || m.file instanceof Blob) {
            newImages.push(m.file);
            if (!primaryImage) primaryImage = m.file;
          } else if (
            typeof m.file === "string" &&
            m.file &&
            !m.file.startsWith("blob:")
          ) {
            remainingImages.push(m.file);
            if (!primaryImage) primaryImage = m.file;
          } else if (
            typeof m.preview === "string" &&
            m.preview &&
            !m.preview.startsWith("blob:")
          ) {
            remainingImages.push(m.preview);
            if (!primaryImage) primaryImage = m.preview;
          }
        } else if (m.type === "video") {
          if (m.file instanceof File || m.file instanceof Blob) {
            newVideo = m.file;
          } else if (
            typeof m.file === "string" &&
            m.file &&
            !m.file.startsWith("blob:")
          ) {
            remainingVideo = m.file;
          }
        }
      });
    }

    if (!primaryImage && imagePreview && !imagePreview.startsWith("blob:")) {
      primaryImage = imagePreview;
    }

    let subCategoryUuid: string | undefined = undefined;
    if (productForm.subcategory) {
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          productForm.subcategory,
        )
      ) {
        subCategoryUuid = productForm.subcategory;
      }
    }

    const matchedSubCat = subCategoriesData.find(
      (sc: any) =>
        sc.id === productForm.subcategory ||
        sc.subCategory === productForm.subcategory ||
        sc.subCategoryName === productForm.subcategory ||
        sc.name === productForm.subcategory,
    );

    const dynamicFields: Record<string, any> = {};
    if (matchedSubCat && Array.isArray(matchedSubCat.fields)) {
      matchedSubCat.fields.forEach((field: string) => {
        dynamicFields[field] = productForm[field] || "";
      });
    }

    const payload: any = {
      itemName: productForm.name,
      sku: productForm.sku ? productForm.sku.trim() : `SKU-${Date.now()}`,
      categoryId: categoryId,
      subCategory: subCategoryUuid,
      price1: Number(productForm.price) || 0,
      discount: Number(productForm.discount) || 0,
      minimumOrder: Number(productForm.minOrder) || 1,
      length: productForm.dimensionsLength || null,
      width: productForm.dimensionsWidth || null,
      height: productForm.dimensionsHeight || null,
      weight: productForm.dimensionsWeight
        ? Number(productForm.dimensionsWeight)
        : 0,
      commodity: productForm.complianceCommodity || undefined,
      importCommodity: productForm.complianceCommodity || undefined,
      dangerousGoods: productForm.features.dangerGoods,
      hasExpiry: productForm.features.expiry,
      hsCode: productForm.complianceHsCode || undefined,
      shortDescription: productForm.shortDesc || "",
      longDescription: productForm.longDesc || "",
      termsAndConditions: productForm.features.terms ? productForm.terms : "",
      storage: storagePayload,
      tags: ["ADMIN"],
      itemInfo: dynamicFields,
      variants: productForm.features.variants
        ? formVariants.map((v) => ({
            name: v.name || "Variant",
            optionValue: v.value,
            price: v.price || productForm.price,
            sku: v.sku || undefined,
            image:
              (v.image as any) instanceof File ||
              (v.image as any) instanceof Blob
                ? v.image
                : typeof v.image === "string" && !v.image.startsWith("data:")
                  ? v.image
                  : undefined,
          }))
        : [],
      images: newImages,
      remainingImages: JSON.stringify(remainingImages),
      video: newVideo,
      remainingVideo: remainingVideo,
      image: primaryImage,
      origin: productForm.features.compliance
        ? productForm.complianceOrigin || undefined
        : undefined,
      destination: productForm.features.compliance
        ? productForm.complianceDestination || undefined
        : undefined,
      tradeType: productForm.features.compliance
        ? productForm.complianceTradeType || undefined
        : undefined,
      dutyPercent:
        productForm.features.compliance && productForm.complianceDuty !== ""
          ? Number(productForm.complianceDuty)
          : undefined,
      vatPercent:
        productForm.features.compliance && productForm.complianceVat !== ""
          ? Number(productForm.complianceVat)
          : undefined,
      restricted: productForm.features.compliance
        ? productForm.complianceRestricted || undefined
        : undefined,
      license: productForm.features.compliance
        ? productForm.complianceLicense || undefined
        : undefined,
      documents: productForm.features.compliance
        ? productForm.complianceDocuments || undefined
        : undefined,
    };

    if (editingProductId) {
      updateProductItem(payload, {
        onSuccess: () => {
          addToast(`Product updated successfully: ${payload.itemName}`);
          queryClient.invalidateQueries({ queryKey: ["itemCategoriesData"] });
          resetProductForm();
          setIsCompilerSidePanelOpen(false);
        },
        onError: (err: any) => {
          const errMsg =
            err?.response?.data?.message ||
            err?.data?.message ||
            err?.message ||
            "Failed to update product";
          addToast(errMsg, "error");
        },
      });
    } else {
      createProductItem(payload, {
        onSuccess: () => {
          addToast(`Product created successfully: ${payload.itemName}`);
          queryClient.invalidateQueries({ queryKey: ["itemCategoriesData"] });
          resetProductForm();
          setIsCompilerSidePanelOpen(false);
        },
        onError: (err: any) => {
          const errMsg =
            err?.response?.data?.message ||
            err?.data?.message ||
            err?.message ||
            "Failed to create product";
          addToast(errMsg, "error");
        },
      });
    }
  };

  // Toggle Status Handler (unchanged)
  const handleToggleStatus = (id: string, currentStatus: string) => {
    const newStatus =
      (currentStatus || "").toUpperCase() === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    updateProductStatus(
      { id, status: newStatus },
      {
        onSuccess: (res: any) => {
          const fallbackMessage =
            newStatus === "ACTIVE"
              ? "Product activated successfully."
              : "Product inactivated successfully.";
          addToast(res?.message || fallbackMessage, "success");
          queryClient.invalidateQueries({ queryKey: ["itemCategoriesData"] });
        },
        onError: (err: any) => {
          addToast(err?.message || "Failed to update status", "error");
        },
      },
    );
  };

  // Export Data Handler (unchanged)
  const handleExportProducts = () => {
    if (!products || products.length === 0) {
      addToast("No products available to export", "info");
      return;
    }
    const exportData = products.map((p, idx) => ({
      "S/N": idx + 1,
      "Item Name": p.name,
      SKU: p.sku,
      Category: p.category,
      "Storage Type": Array.isArray(p.storage)
        ? p.storage
            .map((s: any) => s.type || s.storageType || "BINS")
            .join(", ")
        : p.storage?.type || "BINS",
      Capacity: Array.isArray(p.storage)
        ? p.storage.map((s: any) => s.capacity || 0).join(", ")
        : p.storage?.capacity || 0,
      Weight: p.dimensions?.weight || 0,
      "Hs Code": p.compliance?.hsCode || "—",
      Status: p.status,
    }));
    const ws = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Products");
    writeFile(wb, "products_catalog.xlsx");
  };

  const loadDemoTemplate = () => {
    resetProductForm();
  };

  // Promo handlers (unchanged)
  const handleAddPromo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoForm.merchantId) {
      addToast("Please select a merchant first.", "error");
      return;
    }
    if (!promoForm.sku.trim()) {
      addToast("Please enter a Promo SKU / Code.", "error");
      return;
    }
    if (!promoForm.startDate) {
      addToast("Please select a Start Date.", "error");
      return;
    }
    if (!promoForm.endDate) {
      addToast("Please select an End Date.", "error");
      return;
    }
    if (promoForm.endDate < promoForm.startDate) {
      addToast("End Date cannot be before Start Date.", "error");
      return;
    }
    const validProducts = promoForm.selectedProducts.filter(
      (p) => p.productSku.trim() && p.qty >= 1,
    );
    if (validProducts.length === 0) {
      addToast(
        "At least one product SKU is required for the promo bundle.",
        "error",
      );
      return;
    }

    const productAndQuantity = validProducts.map((vp) => {
      const matchedProduct = products.find(
        (p) => p.sku === vp.productSku || p.id === vp.productSku,
      );
      return {
        productId: matchedProduct?.id || vp.productSku,
        quantity: Number(vp.qty) || 1,
      };
    });

    const targetClientId =
      promoForm.merchantId || promoForm.merchant || clients?.[0]?.id || "";

    const payload = {
      clientType: "LM",
      toClient: targetClientId,
      startEndDate: `${promoForm.startDate} - ${promoForm.endDate}`,
      promoCode: promoForm.sku.trim(),
      productAndQuantity,
    };

    createPromo(payload, {
      onSuccess: () => {
        addToast(`Promo created successfully: ${payload.promoCode}`);
        queryClient.invalidateQueries({ queryKey: ["itemPromoData"] });
        queryClient.invalidateQueries({ queryKey: ["promoItemsData"] });
        queryClient.invalidateQueries();
        setPromoForm({
          merchant: "",
          merchantId: "",
          sku: "",
          selectedProducts: [{ productSku: "", qty: 1 }],
          startDate: "",
          endDate: "",
        });
        setActiveTab("promos");
      },
      onError: (err: any) => {
        addToast(err?.message || "Failed to create promo", "error");
      },
    });
  };

  const deletePromo = (id: string) => {
    updatePromoStatus(
      { id, status: "INACTIVE" },
      {
        onSuccess: () => {
          addToast("Promo set to inactive.", "info");
          queryClient.invalidateQueries({ queryKey: ["itemPromoData"] });
        },
        onError: (err: any) => {
          addToast(err?.message || "Failed to update promo status", "error");
        },
      },
    );
  };

  // Gift handlers (unchanged)
  const resetGiftForm = () => {
    setEditingGiftId(undefined);
    setGiftForm({
      merchant: "",
      merchantId: "",
      giftSku: "",
      mainProductSku: "",
      mainQty: 1,
      giftProductSku: "",
      giftQty: 1,
      startDate: "",
      endDate: "",
      giftItems: [{ sku: "", qty: 1 }],
    });
  };

  const handleAddGift = (e: React.FormEvent) => {
    e.preventDefault();
    if (!giftForm.merchant.trim()) {
      addToast("Please select a merchant first.", "error");
      return;
    }
    if (!giftForm.giftSku.trim()) {
      addToast("Please enter a Gift Campaign SKU.", "error");
      return;
    }
    if (!giftForm.mainProductSku.trim()) {
      addToast("Please select a Purchase Trigger SKU.", "error");
      return;
    }
    if (!giftForm.giftProductSku.trim()) {
      addToast("Please select a Complimentary Gift SKU.", "error");
      return;
    }
    if (giftForm.mainProductSku.trim() === giftForm.giftProductSku.trim()) {
      addToast(
        "Complimentary Gift SKU cannot be the same as Purchase Trigger SKU.",
        "error",
      );
      return;
    }
    if (!giftForm.startDate) {
      addToast("Please select a Start Date.", "error");
      return;
    }
    if (!giftForm.endDate) {
      addToast("Please select an End Date.", "error");
      return;
    }

    const today = new Date().toISOString().split("T")[0];
    if (giftForm.startDate < today) {
      addToast("Start Date cannot be in the past.", "error");
      return;
    }
    if (giftForm.endDate < today) {
      addToast("End Date cannot be in the past.", "error");
      return;
    }
    if (giftForm.endDate < giftForm.startDate) {
      addToast("End Date cannot be before Start Date.", "error");
      return;
    }

    const values = {
      toClient: giftForm.merchantId,
      merchantId: giftForm.merchantId,
      merchant: giftForm.merchant,
      giftSku: giftForm.giftSku.trim(),
      mainProductSku: giftForm.mainProductSku.trim(),
      mainQty: Number(giftForm.mainQty) || 1,
      giftProductSku: giftForm.giftProductSku.trim(),
      giftQty: Number(giftForm.giftQty) || 1,
      giftItems: giftForm.giftItems,
      startDate: giftForm.startDate,
      endDate: giftForm.endDate,
    };

    handleSaveGiftFromForm(values);
  };

  const deleteGift = async (id: string) => {
    try {
      await deleteGiftMutationAsync(id);
      addToast("Gift rule deleted successfully.", "info");
      queryClient.invalidateQueries({ queryKey: ["giftRuleData"] });
    } catch (err: any) {
      addToast(err?.message || "Failed to delete gift rule", "error");
    }
  };

  const isPanelOpen =
    isBulkUploadOpen || isBulkResponseOpen || isCompilerSidePanelOpen;

  const containerContent = (
    <div
      className={`${activeTabExternal ? "" : "px-4"} ${
        isPanelOpen ? "col-span-12 lg:col-span-7" : "col-span-12"
      }`}
    >
      <div className="gap-6 rounded-lg border space-y-3">
        {/* Global Search Bar — visible on all tabs */}
        <div className="flex justify-end items-center px-1.5 py-3.5 border-b">
          <div className="flex flex-wrap items-center gap-3">
            {/* Premium Search */}
            <PremiumSearch
              value={searchFor}
              placeholder={t("Search by name, SKU or promo code...")}
              onChange={(e) => {
                const val = e.target.value;
                setSearchFor(val);
                setPageNo(1);
                // Check if this matches a known promo code
                const trimmed = val.trim().toLowerCase();
                if (trimmed.length >= 2) {
                  const matchedPromo = promos.find(
                    (p) =>
                      (p.sku || p.promoCode || "").toLowerCase() === trimmed,
                  );
                  if (matchedPromo) {
                    setPromoCodeFilter(
                      matchedPromo.sku || matchedPromo.promoCode || val.trim(),
                    );
                    setActiveTab("catalog");
                  } else {
                    setPromoCodeFilter("");
                  }
                } else {
                  setPromoCodeFilter("");
                }
              }}
              onClear={() => {
                setPromoCodeFilter("");
              }}
            />
            {activeTab === "catalog" && (
              <DropdownFilter
                setValue={setDropdownValue}
                value={value}
                filterheadings={filterHeadings}
              />
            )}
            {activeTab === "catalog" && (
              <div className="flex flex-wrap items-center gap-2">
                <ButtonStyled
                  onClick={() => {
                    setIsCompilerSidePanelOpen(false);
                    setIsBulkUploadOpen(true);
                  }}
                  variant="outline"
                  value={t("Import Bulk")}
                  title="Import products in bulk"
                />
                <ButtonStyled
                  onClick={handleExportProducts}
                  variant="outline"
                  value={t("Export")}
                  title="Export catalog data"
                />
                <ButtonStyled
                  onClick={() => {
                    resetProductForm();
                    setActiveTab("catalog");
                    setIsBulkUploadOpen(false);
                    setIsBulkResponseOpen(false);
                    setIsCompilerSidePanelOpen(true);
                  }}
                  variant={isCompilerSidePanelOpen ? "solid" : "outline"}
                  value={t("Add Item")}
                />
              </div>
            )}
          </div>
        </div>
        {activeTab === "catalog" && (
          <div className="p-2  max-h-[calc(100vh-16rem)] overflow-hidden overflow-y-auto">
            {promoCodeFilter && (
              <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-xl text-xs font-semibold text-yellow-800">
                <span className="text-yellow-500">🏷️</span>
                <span>
                  {t("Showing products for promo code:")}{" "}
                  <span className="font-bold text-yellow-900 uppercase">
                    {promoCodeFilter}
                  </span>
                </span>
                <button
                  onClick={() => {
                    setPromoCodeFilter("");
                    setSearchFor("");
                  }}
                  className="ml-auto flex items-center gap-1 text-yellow-600 hover:text-yellow-900 transition-colors"
                >
                  <IconX size={13} stroke={2.5} />
                  {t("Clear filter")}
                </button>
              </div>
            )}
            <Catalog
              products={products}
              isLoading={isProductsLoading}
              searchQuery={searchQuery}
              categoryFilter={value}
              total={totalItems}
              pages={totalPages}
              pageNo={pageNo}
              compact={isPanelOpen}
              onPageChange={(page) => setPageNo(page)}
              onEditProduct={handleEditProduct}
              onViewProduct={handleViewProduct}
              onToggleStatus={handleToggleStatus}
              onDeletePromo={deletePromo}
              onDeleteGift={deleteGift}
              onAddProductClick={() => {
                resetProductForm();
                setActiveTab("catalog");
                setIsBulkUploadOpen(false);
                setIsBulkResponseOpen(false);
                setIsCompilerSidePanelOpen(true);
              }}
              promos={promos}
              gifts={gifts}
              onEditPromo={handleEditPromo}
              onEditGift={handleEditGift}
              onTogglePromoStatus={handleTogglePromoStatus}
              onToggleGiftStatus={handleToggleGiftStatus}
            />
          </div>
        )}

        {activeTab === "promos" && (
          <Promos
            products={products}
            promos={promos}
            isLoading={isPromoLoading}
            isSubmitting={isPromoCreating}
            clients={clients}
            promoForm={promoForm}
            onPromoFormChange={(updates) =>
              setPromoForm((prev) => ({ ...prev, ...updates }))
            }
            onAddPromo={handleAddPromo}
            onDeletePromo={deletePromo}
            onCancel={() => setActiveTab("catalog")}
          />
        )}

        {activeTab === "gifts" && (
          <Gifts
            products={products}
            gifts={gifts}
            clients={clients}
            giftForm={giftForm}
            onGiftFormChange={(updates) =>
              setGiftForm((prev) => ({ ...prev, ...updates }))
            }
            onAddGift={handleAddGift}
            onDeleteGift={deleteGift}
            onCancel={() => {
              resetGiftForm();
              setActiveTab("catalog");
            }}
            editingGiftId={editingGiftId}
            onEditGift={handleEditGift}
          />
        )}
        {activeTab === "productFetch" && (
          <ProductFetch
            setActiveTab={function (value: React.SetStateAction<string>): void {
              setActiveTab(value as any);
            }}
            onFetchStatsChange={onFetchStatsChange}
          />
        )}
      </div>
    </div>
  );

  const sidePanel = isPanelOpen ? (
    <div className="col-span-12 lg:col-span-5 space-y-4">
      {isBulkUploadOpen && (
        <ItemBulkUploadModals
          onClose={() => setIsBulkUploadOpen(false)}
          onResponseOpen={() => setIsBulkResponseOpen(true)}
          setItemBulkResponse={setItemBulkResponse}
          refetchTable={() => {
            queryClient.invalidateQueries({ queryKey: ["itemCategoriesData"] });
          }}
        />
      )}

      {isBulkResponseOpen && itemBulkResponse && (
        <ItemBulkResponseModals
          onClose={() => {
            setIsBulkResponseOpen(false);
            setItemBulkResponse(null);
          }}
          data={itemBulkResponse}
        />
      )}

      {isCompilerSidePanelOpen && (
        <div className="bg-white/20 rounded-lg border border-slate-200 shadow-sm ">
          <div className="flex justify-between items-center px-4 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              {sidebarActiveTab === "Product Details" ||
              editingProductId ||
              editingGiftId ||
              editingPromoId ? (
                <h2 className="text-sm font-bold text-[#212121]">
                  {sidebarActiveTab === "Product Details"
                    ? t("Product Details")
                    : sidebarActiveTab === "Product"
                      ? t("Edit Item")
                      : sidebarActiveTab === "Promos"
                        ? t("Edit Promo Rule")
                        : sidebarActiveTab === "Gifts"
                          ? t("Edit Gift Rule")
                          : t(sidebarActiveTab)}
                </h2>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-slate-500">
                    {t("Select Section:")}
                  </label>
                  <select
                    value={sidebarActiveTab}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSidebarActiveTab(val);
                    }}
                    className="bg-yellow-50 border border-gray-300 text-slate-800 text-xs font-semibold rounded-lg px-3 py-1.5 outline-none focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037] cursor-pointer"
                  >
                    <option value="Product">{t("Product")}</option>
                    <option value="Promos">{t("Promos")}</option>
                    <option value="Gifts">{t("Gifts")}</option>
                    <option value="Product Fetch">{t("Product Fetch")}</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {sidebarActiveTab === "Product Details" && viewingProduct && (
                <>
                  <ButtonStyled
                    onClick={() => handleEditProduct(viewingProduct)}
                    variant="solid"
                    icon={<IconPencil size={14} />}
                    value={t("Edit Item")}
                  />

                  <button
                    type="button"
                    onClick={() => {
                      setIsCompilerSidePanelOpen(false);
                      setViewingProduct(null);
                    }}
                    className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
                    title="Close"
                  >
                    <IconX size={16} stroke={1.5} />
                  </button>
                </>
              )}

              {sidebarActiveTab === "Product" && (
                <>
                  <ButtonStyled
                    onClick={handleAddProduct}
                    disabled={isCreating || isUpdating}
                    isPending={isCreating || isUpdating}
                    variant="outline"
                    value={editingProductId ? t("Update Item") : t("Add Item")}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      resetProductForm();
                      setIsCompilerSidePanelOpen(false);
                    }}
                    className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
                    title="Close"
                  >
                    <IconX size={16} stroke={1.5} />
                  </button>
                </>
              )}

              {sidebarActiveTab === "Promos" && (
                <>
                  <ButtonStyled
                    onClick={handleSubmitPromo(onSubmitPromo)}
                    disabled={isPromoCreating}
                    isPending={isPromoCreating}
                    variant="solid"
                    value={t("Save")}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setEditingPromoId(undefined);
                      setIsCompilerSidePanelOpen(false);
                      setSidebarActiveTab("Product");
                    }}
                    className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
                    title="Close"
                  >
                    <IconX size={16} stroke={1.5} />
                  </button>
                </>
              )}

              {sidebarActiveTab === "Gifts" && (
                <>
                  <ButtonStyled
                    onClick={() => createGiftRef.current?.submit()}
                    variant="solid"
                    value={t("Save")}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setEditingGiftId(undefined);
                      setSelectedGiftForEdit(undefined);
                      setIsCompilerSidePanelOpen(false);
                      setSidebarActiveTab("Product");
                    }}
                    className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
                    title="Close"
                  >
                    <IconX size={16} stroke={1.5} />
                  </button>
                </>
              )}

              {sidebarActiveTab === "Product Fetch" && (
                <div className="flex items-center gap-1">
                  <ProductFetch
                    setActiveTab={() => setSidebarActiveTab("Product")}
                    compact
                  />
                  <button
                    type="button"
                    onClick={() => setSidebarActiveTab("Product")}
                    className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
                    title="Close"
                  >
                    <IconX size={16} stroke={1.5} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {sidebarActiveTab === "Product Details" && viewingProduct && (
            <ProductDetailView
              product={viewingProduct}
              promos={promos}
              gifts={gifts}
              onEditProduct={handleEditProduct}
              onEditPromo={handleEditPromo}
              onEditGift={handleEditGift}
              onTogglePromoStatus={handleTogglePromoStatus}
              onToggleGiftStatus={handleToggleGiftStatus}
              onClose={() => {
                setIsCompilerSidePanelOpen(false);
                setViewingProduct(null);
              }}
            />
          )}

          {sidebarActiveTab === "Product" && (
            <Compiler
              categories={categoriesData}
              isSubmitting={isCreating || isUpdating}
              editingProductId={editingProductId}
              productForm={productForm}
              formVariants={formVariants}
              imagePreview={imagePreview}
              financialMetrics={financialMetrics}
              errors={formErrors}
              onProductFormChange={(updates) => {
                setProductForm({ ...productForm, ...updates });
                const newErrors = { ...formErrors };
                Object.keys(updates).forEach((key) => delete newErrors[key]);
                setFormErrors(newErrors);
              }}
              onVariantsChange={setFormVariants}
              onImagePreviewChange={setImagePreview}
              onLoadDemo={loadDemoTemplate}
              onDiscard={() => {
                resetProductForm();
                setIsCompilerSidePanelOpen(false);
              }}
              onSubmit={handleAddProduct}
              onSuccess={() => {}}
              hideHeaderButtons={true}
            />
          )}

          {sidebarActiveTab === "Promos" && (
            <CreatePromoModal
              open={true}
              onClose={() => {
                setEditingPromoId(undefined);
                setIsCompilerSidePanelOpen(false);
                setSidebarActiveTab("Product");
              }}
              setValue={setPromoValue}
              watch={watchPromo}
              register={registerPromo}
              errors={promoErrors}
              clearErrors={clearPromoErrors}
              onSubmit={handleSubmitPromo(onSubmitPromo)}
              loading={isPromoCreating}
              mode={editingPromoId ? "edit" : "add"}
              clients={clients}
              inline={true}
              hideHeader={true}
            />
          )}

          {sidebarActiveTab === "Gifts" && (
            <CreateGiftForm
              ref={createGiftRef}
              open={true}
              onClose={() => {
                setEditingGiftId(undefined);
                setSelectedGiftForEdit(undefined);
                setIsCompilerSidePanelOpen(false);
                setSidebarActiveTab("Product");
              }}
              inline={true}
              hideHeader={true}
              editData={selectedGiftForEdit}
              onSubmit={handleSaveGiftFromForm}
            />
          )}

          {sidebarActiveTab === "Product Fetch" && (
            <ProductFetch
              setActiveTab={() => {
                setSidebarActiveTab("Product");
              }}
              compactTop
            />
          )}
        </div>
      )}
    </div>
  ) : null;

  if (activeTabExternal) {
    return (
      <div className="relative grid grid-cols-12 gap-4 bg-gradient-to-b from-[#FFFDF5] to-[#FFF7D6] backdrop-blur-xl transition-all duration-300 rounded-lg w-full p-3">
        {containerContent}
        {sidePanel}
      </div>
    );
  }

  return (
    <div className="px-4 space-y-0 pb-8">
      <div className="space-y-0">
        <div className="flex justify-between items-center flex-wrap lg:flex-nowrap w-full">
          <div className="flex items-center gap-4 flex-wrap lg:flex-nowrap">
            <RoleToggle
              options={["Product", "Promos", "Gifts", "Product Fetch"]}
              active={
                activeTab === "catalog"
                  ? "Product"
                  : activeTab === "promos"
                    ? "Promos"
                    : activeTab === "gifts"
                      ? "Gifts"
                      : "Product Fetch"
              }
              onChange={(label) => {
                if (label === "Product") setActiveTab("catalog");
                if (label === "Promos") setActiveTab("promos");
                if (label === "Gifts") setActiveTab("gifts");
                if (label === "Product Fetch") setActiveTab("productFetch");
              }}
              activeClassName="bg-[#000000] text-[#ffffff] font-bold shadow-sm"
              inactiveClassName="bg-transparent text-[#212121]/70 font-bold"
              containerClassName="inline-flex rounded-t-lg rounded-tr-[20px] bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 py-0.5 px-1 overflow-x-auto hide-scrollbar max-w-full"
            />
          </div>
        </div>
      </div>
      <div className="relative grid grid-cols-12 gap-4 bg-gradient-to-b from-[#FFFDF5] to-[#FFF7D6] backdrop-blur-xl transition-all duration-300 rounded-lg w-full">
        {containerContent}
        {sidePanel}
      </div>
    </div>
  );
}
